import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { UsersPageList } from './list';
import { setLoading } from '../../redux/slices/appInfo';
import { SearchParamsStateType, useFilter } from '../../hooks/useFilter';
import { DEFAULT_PAGE_SIZE } from '../../utils/constants';
import { removeUndefinedAttribute } from '../../utils';
import {
    Direction,
    IPagination,
    SearchQueryParams,
} from '../../interface/paging';
import { TUsers } from './type';
import { getUsers } from '../../api/setting/users';

const Wrapper = styled.div``;

type TSortData = {
    field?: string;
    order?: 'asc' | 'desc';
};
type TSortDataContext = {
    sortData: TSortData;
    setSortData: (data: TSortData) => void;
};
const SortDataContext = React.createContext<TSortDataContext>(
    {} as TSortDataContext
);
export const useSortData = () => React.useContext(SortDataContext);

export const UsersPage: React.FC = () => {
    const dispatch = useDispatch();
    const [pagination, setPagination] = React.useState<IPagination | undefined>(
        undefined
    );
    const filtersDefaults: SearchParamsStateType = {
        pageNum: { type: 'number', default: 1 },
        pageSize: { type: 'number', default: DEFAULT_PAGE_SIZE },
        order: { type: 'string', default: Direction.DESC },
        orderBy: { type: 'string', default: 'Username' },
    };
    const [filterParams, setFilterParams] = useFilter(filtersDefaults);
    const [sortData, setSortData] = React.useState<TSortData>({
        field: 'Username',
        order: 'desc',
    });
    const onChangeSort = React.useCallback(
        (data: TSortData) => {
            setSortData(data);
            const searchQueryParams: SearchQueryParams = {
                page: 1,
                pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
                sortBy: data.order as Direction,
                orderBy: data.field,
            };
            onLoadData(searchQueryParams);
        },
        [filterParams]
    );
    const [userList, setUserList] = React.useState<TUsers[]>([]);

    const onLoadData = React.useCallback(
        async (queryParams: SearchQueryParams | undefined) => {
            try {
                dispatch(setLoading(true));
                const newPayload = removeUndefinedAttribute(queryParams);
                const result = await getUsers(newPayload);
                setFilterParams(newPayload);
                if (result.status === 200) {
                    setUserList(result?.data?.data ?? []);
                }
            } catch (error) {
                // Handle error
            } finally {
                dispatch(setLoading(false));
            }
        },
        [dispatch]
    );

    const onChangePaging = React.useCallback(
        (values: any) => {
            const searchQueryParams: SearchQueryParams = {
                page: values.page,
                pageSize: pagination?.pageSize ?? 10,
                sortBy: pagination?.order as Direction,
                orderBy: pagination?.orderBy ?? 'Username',
            };
            onLoadData(searchQueryParams);
        },
        [pagination]
    );

    React.useEffect(() => {
        onLoadData({
            page: 1,
            pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
            sortBy: Direction.DESC,
        });
    }, []);

    const onReload = React.useCallback(() => {
        onLoadData({
            page: 1,
            pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
            sortBy: filterParams.order as Direction,
            orderBy: filterParams.orderBy,
        });
    }, [filterParams]);

    return (
        <Wrapper className="flex flex-col items-center justify-start h-full">
            <div className="flex flex-row items-start h-full w-full">
                <SortDataContext.Provider
                    value={{ sortData, setSortData: onChangeSort }}
                >
                    <UsersPageList
                        userList={userList}
                        setUserList={setUserList}
                        pagination={pagination}
                        onChangePaging={onChangePaging}
                        onReload={onReload}
                        filterParams={filterParams}
                        onFilterData={onLoadData}
                    />
                </SortDataContext.Provider>
            </div>
        </Wrapper>
    );
};
